import { SearchOutlined } from '@ant-design/icons';
import { Card, List, Row, Space, Tag, Typography } from 'antd';
import { FilterDropdownProps } from 'antd/es/table/interface';
import config from 'config';
import { useGetLocationsLazyQuery, useGetLocationsQuery } from 'gql/types';
import { useDebounce } from 'modules/common/hooks';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

import Button from '../Button';
import Checkbox from '../Checkbox';
import Input from '../Input';
import Spin from '../Spin';

type LocationFilterDropdownProps = FilterDropdownProps;

export const LocationFilterDropdown: FC<LocationFilterDropdownProps> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  visible,
}) => {
  const { t } = useTranslation('common.LocationFilterDropdown');

  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [onlyAssignedLocations, setOnlyAssignedLocations] = useState(false);
  const [allLoadedLocations, setAllLoadedLocations] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [hasMore, setHasMore] = useState(true);
  const [isSearchPending, setIsSearchPending] = useState(false);

  const currentUser = useCurrentUser();

  const [localSelected, setLocalSelected] = useState<string[]>(
    (selectedKeys as string[]) || []
  );

  const isInitializedRef = useRef(false);

  const sortedLocations = useMemo(() => {
    return [...allLoadedLocations].sort((a, b) => a.name.localeCompare(b.name));
  }, [allLoadedLocations]);

  const assignedLocations = useMemo(() => {
    return currentUser.data?.assignedLocations ?? [];
  }, [currentUser.data?.assignedLocations]);

  const mappedLocations = useMemo(() => {
    return [...sortedLocations].map((location) => ({
      ...location,
      assigned: assignedLocations.some((e) => e.id === location.id),
    }));
  }, [sortedLocations, assignedLocations]);

  const { data: initialData, loading: initialLoading } = useGetLocationsQuery({
    variables: {
      input: {
        take: config.PAGINATION.LOCATIONS_PAGE_SIZE,
        skip: 0,
      },
    },
    onCompleted: (data) => {
      const locations = data.getLocations.data || [];
      setAllLoadedLocations(locations);
      setHasMore(locations.length === config.PAGINATION.LOCATIONS_PAGE_SIZE);
    },
  });

  useEffect(() => {
    if (visible) {
      setSearchValue('');
      setCurrentPage(1);
      setIsSearchPending(false);
      setOnlyAssignedLocations(false);
      setLocalSelected((selectedKeys as string[]) || []);
      isInitializedRef.current = true;

      const locations = initialData?.getLocations.data || [];
      setAllLoadedLocations(locations);
      setHasMore(locations.length === config.PAGINATION.LOCATIONS_PAGE_SIZE);
    }
  }, [visible]);

  const [fetchLocations, { loading: searchLoading }] =
    useGetLocationsLazyQuery();

  const isSearching = isSearchPending || searchLoading;
  const isInitialLoading = initialLoading && allLoadedLocations.length === 0;

  const { debounce } = useDebounce(300);

  const performSearch = useCallback(
    async (params: { query: string; assignedOnly: boolean }) => {
      setCurrentPage(1);
      try {
        const { data } = await fetchLocations({
          variables: {
            input: {
              take: config.PAGINATION.LOCATIONS_PAGE_SIZE,
              skip: 0,
              filter: {
                searchingQuery: params.query || undefined,
                assignedOnly: params.assignedOnly,
              },
            },
          },
        });

        const locations = data?.getLocations.data || [];
        setAllLoadedLocations(locations);
        setHasMore(locations.length === config.PAGINATION.LOCATIONS_PAGE_SIZE);
      } finally {
        setIsSearchPending(false);
      }
    },
    [fetchLocations]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setIsSearchPending(true);
    debounce(performSearch)({
      query: value,
      assignedOnly: onlyAssignedLocations,
    });
  };

  const filterAssignedLocations = (selectedLocations: string[]) => {
    return selectedLocations.filter((location) =>
      assignedLocations.some((assigned) => assigned.name === location)
    );
  };

  const handleAssignedLocationFilter = (isAssignedOnly: boolean) => {
    setOnlyAssignedLocations(isAssignedOnly);
    if (isAssignedOnly) {
      const filtered = filterAssignedLocations(localSelected);
      setSelectedKeys(filtered);
    }
    setIsSearchPending(true);
    debounce(performSearch)({
      query: searchValue,
      assignedOnly: isAssignedOnly,
    });
  };

  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    const skip = (nextPage - 1) * config.PAGINATION.LOCATIONS_PAGE_SIZE;

    const { data } = await fetchLocations({
      variables: {
        input: {
          take: config.PAGINATION.LOCATIONS_PAGE_SIZE,
          skip,
          filter: {
            searchingQuery: searchValue || undefined,
            assignedOnly: onlyAssignedLocations,
          },
        },
      },
    });

    const newLocations = data?.getLocations.data || [];

    setAllLoadedLocations((prev) => {
      const existingIds = new Set(prev.map((loc) => loc.id));
      const uniqueNewLocations = newLocations.filter(
        (loc: any) => !existingIds.has(loc.id)
      );
      return [...prev, ...uniqueNewLocations];
    });

    setCurrentPage(nextPage);
    setHasMore(newLocations.length === config.PAGINATION.LOCATIONS_PAGE_SIZE);
  };

  const handleCheckboxChange = (locationName: string, checked: boolean) => {
    setLocalSelected((prev) => {
      const updated = checked
        ? [...prev, locationName]
        : prev.filter((name) => name !== locationName);
      return updated;
    });
  };

  const handleReset = () => {
    setSearchValue('');
    setOnlyAssignedLocations(false);
    setLocalSelected([]);
    setSelectedKeys([]);
    setCurrentPage(1);
    clearFilters?.();

    const locations = initialData?.getLocations.data || [];
    setAllLoadedLocations(locations);
    setHasMore(locations.length === config.PAGINATION.LOCATIONS_PAGE_SIZE);
  };

  const handleConfirm = () => {
    const selectedAssignedLocations = filterAssignedLocations(localSelected);
    const selectedLocations = onlyAssignedLocations
      ? selectedAssignedLocations.length === 0
        ? assignedLocations.map((e) => e.name)
        : selectedAssignedLocations
      : localSelected;
    setSelectedKeys(selectedLocations);
    setOnlyAssignedLocations(false);
    confirm();
  };

  const renderContent = () => {
    if (isInitialLoading) {
      return (
        <Row
          justify="center"
          align="middle"
          className={styles.spinnerContainer}
        >
          <Spin size="small" />
        </Row>
      );
    }

    if (!isSearching && allLoadedLocations.length === 0) {
      return (
        <Typography.Text type="secondary" className={styles.noData}>
          {t('noLocationsFound')}
        </Typography.Text>
      );
    }

    return (
      <Spin spinning={isSearching} size="small">
        <List
          className={styles.checkboxList}
          dataSource={mappedLocations}
          renderItem={(location) => (
            <List.Item className={styles.checkboxItem} key={location.id}>
              <Checkbox
                checked={localSelected.includes(location.name)}
                onChange={(e) =>
                  handleCheckboxChange(location.name, e.target.checked)
                }
              >
                <Space>
                  <span>{location.name}</span>
                  {location.assigned && <Tag color="blue">{t('assigned')}</Tag>}
                </Space>
              </Checkbox>
            </List.Item>
          )}
          loadMore={
            hasMore && (
              <Row justify="center" className={styles.loadMoreContainer}>
                <Button
                  type="link"
                  onClick={handleLoadMore}
                  loading={searchLoading}
                >
                  {t('loadMore')}
                </Button>
              </Row>
            )
          }
        />
      </Spin>
    );
  };

  return (
    <Card className={styles.filterDropdown} size="small" bordered={false}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Input
          placeholder={t('searchLocations')}
          value={searchValue}
          onChange={handleSearchChange}
          prefix={<SearchOutlined />}
          allowClear
        />

        <Row>
          <Checkbox
            checked={onlyAssignedLocations}
            onChange={(e) => handleAssignedLocationFilter(e.target.checked)}
          >
            {t('onlyAssigned')}
          </Checkbox>
        </Row>

        <div className={styles.listContainer}>{renderContent()}</div>

        <Row justify="space-between">
          <Button size="small" onClick={handleReset}>
            {t('reset')}
          </Button>
          <Button type="primary" size="small" onClick={handleConfirm}>
            {t('ok')}
          </Button>
        </Row>
      </Space>
    </Card>
  );
};
